import React, { useEffect, useMemo, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { Row, Col, Card, Typography, Divider, Image, Space, Button, Flex, message } from "antd"
import PropTypes from "prop-types"
import moment from "moment"
import EmbeddedExperienceCard from "../ExperienceCard"
import BookingConfirm from "assets/icons/booking-confirm.svg"
import { Icon } from "ui/Icon"
import { formatTimeDuration } from "utils/experience"
import EmbeddedBookingLayout from "../EmbeddedBookingLayout"
import styles from "./BookingConfirmation.module.css"
import { cleanedExperiences, getBookingDetailsById } from "store/experiences"
import { routes } from "router"
import { sendInstruction } from "store/confirm-password"
import { ShareListingModal } from "pagesV2/pro/Experiences/Home/ShareModal"
import { numberToCurrency } from "helpers/string"
import { Text } from "ui/Typography"
import { colors } from "themes/variables"
import { useTranslation } from "react-i18next"
import Spinner from "components/common/Spinner"
import EmbeddedPageHeader from "../EmbeddedPageHeader"
import { useTheme } from "context/AntdThemeContext"
import Stepper from "./Stepper"
import { isFeatureEnabled } from "config/features"
const { Title } = Typography
const BookingConfirmation = ({ mode }) => {
  const navigate = useNavigate()
  const { uuid, id } = useParams()
  const dispatch = useDispatch()
  const loggedIn = localStorage?.getItem("access_token")
  const { confirmedBookingData, bookingInfo } = useSelector((state) => state.experiences)
  const bookingQuestionnaireId = confirmedBookingData?.data?.booking_questionnaire_id
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const { t } = useTranslation()
  const { switchTheme } = useTheme()

  const handleOpen = () => {
    setIsShareModalOpen(true)
  }

  const handleClose = () => {
    setIsShareModalOpen(false)
  }

  const handleClick = () => {
    const token = bookingInfo?.data?.guest_token
    if (token) {
      dispatch(sendInstruction(token))
      message.success(t("embedded.booking_confirmation.instruction_sent_successfully"))
    } else {
      message.error(t("embedded.booking_confirmation.token_not_found"))
    }
  }

  const guestSummary = useMemo(() => {
    const {
      adult_count,
      child_count,
      infant_count,
      passenger_count,
      extra_adult_count,
      extra_child_count,
      extra_infant_count,
      extra_passenger_count
    } = confirmedBookingData?.data || {}

    const formatGroup = (counts = {}) => {
      const parts = []
      if (counts.adult > 0) parts.push(`${counts.adult} ${counts.adult === 1 ? "Adult" : "Adults"}`)
      if (counts.child > 0) parts.push(`${counts.child} ${counts.child === 1 ? "Child" : "Children"}`)
      if (counts.infant > 0) parts.push(`${counts.infant} ${counts.infant === 1 ? "Infant" : "Infants"}`)
      if (counts.passenger > 0) parts.push(`${counts.passenger} ${counts.passenger === 1 ? "Passenger" : "Passengers"}`)
      return parts.length > 0 ? parts.join(", ") : null
    }

    const mainGuests = formatGroup({
      adult: adult_count,
      child: child_count,
      infant: infant_count,
      passenger: passenger_count
    })

    const extraGuests = formatGroup({
      adult: extra_adult_count,
      child: extra_child_count,
      infant: extra_infant_count,
      passenger: extra_passenger_count
    })

    return { mainGuests, extraGuests }
  }, [confirmedBookingData?.data])

  const STATUS_MESSAGES = {
    draft: t("embedded.booking_confirmation.draft"),
    booked: t("embedded.booking_confirmation.booked"),
    completed: t("embedded.booking_confirmation.completed"),
    canceled: t("embedded.booking_confirmation.canceled"),
    requested: t("embedded.booking_confirmation.requested"),
    refunded: t("embedded.booking_confirmation.refunded")
  }

  const getStatusTag = (status) => {
    switch (status) {
      case "draft":
        return (
          <Text type="secondary" className="rounded-full" style={{ background: "#E6E6E6", padding: "2px 8px" }}>
            {t("embedded.booking_confirmation.status.draft")}
          </Text>
        )
      case "booked":
        return (
          <Text type="success" className="rounded-full" style={{ background: "#55F41D1A", padding: "2px 8px" }}>
            {t("embedded.booking_confirmation.status.booked")}
          </Text>
        )
      case "completed":
        return (
          <Text type="success" className="rounded-full" style={{ background: "#D4EDDA", padding: "2px 8px" }}>
            {t("embedded.booking_confirmation.status.completed")}
          </Text>
        )
      case "canceled":
        return (
          <Text type="danger" className="rounded-full" style={{ background: "#F8D7DA", padding: "2px 8px" }}>
            {t("embedded.booking_confirmation.status.canceled")}
          </Text>
        )
      case "requested":
        return (
          <Text type="warning" className="rounded-full" style={{ background: "#FFF6E6", padding: "2px 8px" }}>
            {t("embedded.booking_confirmation.status.requested")}
          </Text>
        )
      case "refunded":
        return (
          <Text type="warning" className="rounded-full" style={{ background: "#FFF3CD", padding: "2px 8px" }}>
            {t("embedded.booking_confirmation.status.refunded")}
          </Text>
        )
      default:
        return null
    }
  }

  const summaryDetails = useMemo(() => {
    const summary = confirmedBookingData?.data?.payment_summary ?? {}
    const {
      tax_and_fee,
      adult,
      child,
      infant,
      passenger,
      pricing_type,
      extra_adult,
      extra_child,
      extra_infant,
      extra_passenger,
      flat_rate
    } = summary

    let subtotal = 0
    const details = []

    const addDetail = (label, count, price) => {
      if (count && price) {
        details.push({ label: `${label} x ${count}`, value: price })
        subtotal += price
      }
    }

    const handleChargePerPerson = () => {
      addDetail("Adult", adult?.count, adult?.price)
      addDetail("Child", child?.count, child?.price)
      addDetail("Infant", infant?.count, infant?.price)
      addDetail("Passenger", passenger?.count, passenger?.price)
    }

    const handleChargeFlatRate = () => {
      const flatRateValue = flat_rate ?? 0
      details.push({ label: "Flat Rate For Main Guests", value: flatRateValue })
      subtotal += flatRateValue

      addDetail("Extra Adult", extra_adult?.count, extra_adult?.price)
      addDetail("Extra Child", extra_child?.count, extra_child?.price)
      addDetail("Extra Infant", extra_infant?.count, extra_infant?.price)
      addDetail("Extra Passenger", extra_passenger?.count, extra_passenger?.price)
    }

    if (pricing_type === "charge_per_person") {
      handleChargePerPerson()
    } else if (pricing_type === "charge_flat_rate") {
      handleChargeFlatRate()
    }

    details.push({ label: "Subtotal", value: subtotal }, { label: "Tax & Fees", value: tax_and_fee ?? 0 })

    return details
  }, [confirmedBookingData?.data?.payment_summary])

  const depositAmount =
    ((confirmedBookingData?.data?.booked_amount || 0) * (confirmedBookingData?.data?.trip?.deposit_percentage || 0)) / 100
  const balanceAmount = (confirmedBookingData?.data?.booked_amount || 0) - depositAmount

  useEffect(() => {
    dispatch(getBookingDetailsById(id))
  }, [id, dispatch])

  // Apply theme based on guide's adventure_mode
  useEffect(() => {
    if (confirmedBookingData?.data?.trip?.guide?.adventure_mode) {
      switchTheme(confirmedBookingData.data.trip.guide.adventure_mode)
    }
  }, [confirmedBookingData?.data?.trip?.guide?.adventure_mode, switchTheme])

  if (confirmedBookingData?.loading) {
    return <Spinner className={styles.bookingConfirmSpinner} />
  }

  return (
    <>
      <EmbeddedPageHeader />
      <EmbeddedBookingLayout type={"bookingConfirm"} mode={mode}>
        <Flex vertical align="center">
          <img src={BookingConfirm} alt="booking" />
          <Title level={4} className="mt-m">
            {STATUS_MESSAGES[confirmedBookingData?.data?.status] ?? "Your booking status is unknown."}
          </Title>
          {confirmedBookingData?.data?.status === "requested" || confirmedBookingData?.data?.status === "draft" ? (
            <Text className="text-center" fw="500" size="lg" style={{ maxWidth: "800px" }}>
              {t("embedded.booking_confirmation.booking_request_message")}
            </Text>
          ) : (
            <Text className="text-center" color={colors.textSecondary} fw="500" size="lg" style={{ maxWidth: "800px" }}>
              {t("embedded.booking_confirmation.booking_confirmation_message")}
            </Text>
          )}
          <div className="d-flex flex-row align-items-center justify-content-center flex-wrap gap-2 mt-xl">
            <Button
              type="primary"
              className="p-l"
              onClick={() => {
                if (mode) {
                  navigate(routes?.rootPath())
                } else {
                  navigate(routes?.embeddedExperienceListing({ uuid: uuid || id }))
                }
                dispatch(cleanedExperiences())
              }}
            >
              {t("embedded.booking_confirmation.browse_more_experiences")}
            </Button>
            {mode && (
              <>
                {loggedIn && (
                  <>
                    <Button
                      color="primary"
                      variant="outlined"
                      className="p-l"
                      onClick={() => {
                        navigate(routes.clientBookingPath({ uuid: id }))
                      }}
                    >
                      {t("embedded.booking_confirmation.view_booking_details")}
                    </Button>
                    <Button color="primary" variant="outlined" className="p-l" onClick={handleOpen}>
                      {t("embedded.booking_confirmation.share")}
                    </Button>
                  </>
                )}
              </>
            )}
            <ShareListingModal
              visible={isShareModalOpen}
              onClose={handleClose}
              listingTitle={confirmedBookingData?.data?.trip?.title}
              listingAddress={confirmedBookingData?.data?.trip?.address}
              listingImage={confirmedBookingData?.data?.trip?.media?.find((item) => item?.position === 0)?.preview_url}
              listingLink={`${global.location.origin}/#/trips/${confirmedBookingData?.data?.trip?.uuid || confirmedBookingData?.data?.trip?.id}`}
            />
          </div>
        </Flex>
        {isFeatureEnabled("CUSTOM_TEMPLATE") && <Stepper bookingQuestionnaireId={bookingQuestionnaireId} />}
        <Row gutter={[24, 24]} className="mt-5xl">
          <Col lg={16} xl={15} span={24}>
            <Card className="rounded-l border-0" style={{ background: "#F9FBFB" }}>
              <Row justify="space-between" align="middle">
                <Col span={24}>
                  <Flex gap={10} wrap="wrap">
                    <Title level={4}>{confirmedBookingData?.data?.trip?.title}</Title>
                    {getStatusTag(confirmedBookingData?.data?.status)}
                  </Flex>
                </Col>
              </Row>
              <Flex gap={10} vertical className={`${styles.confirmationDetails} mt-m`}>
                <Image
                  className={styles.confirmationImg}
                  src={confirmedBookingData?.data?.trip?.media?.find((item) => item?.position === 0)?.preview_url}
                  preview={false}
                />
                <Row gutter={[0, 8]} className="flex-1">
                  <Col span={6}>
                    <Text>Date</Text>
                  </Col>
                  <Col span={18}>
                    <Text>{moment(confirmedBookingData?.data?.booked_date)?.format("MM-DD-YYYY")}</Text>
                  </Col>
                  <Col span={6}>
                    <Text>Guests</Text>
                  </Col>
                  <Col span={18}>
                    <Text>{guestSummary?.mainGuests}</Text>
                  </Col>
                  {confirmedBookingData?.data?.trip_schedule?.extra_guests_allowed && (
                    <>
                      <Col span={6}>
                        <Text>Extra Guests</Text>
                      </Col>
                      <Col span={18}>
                        <Text>{guestSummary?.extraGuests ?? "-"}</Text>
                      </Col>
                    </>
                  )}
                  <Col span={6}>
                    <Text>Trip Length</Text>
                  </Col>
                  <Col span={18}>
                    <Text>{formatTimeDuration(confirmedBookingData?.data?.trip_schedule?.trip_duration)}</Text>
                  </Col>
                  <Col span={6}>
                    <Text>Time</Text>
                  </Col>
                  <Col span={18}>
                    <Text>{`${confirmedBookingData?.data?.start_time} – ${confirmedBookingData?.data?.end_time}`}</Text>
                  </Col>
                  <Col span={6}>
                    <Text>Address</Text>
                  </Col>
                  <Col span={18}>
                    <Text>{confirmedBookingData?.data?.trip?.address}</Text>
                  </Col>
                  <Col span={6}>
                    <Text>Booking ID</Text>
                  </Col>
                  <Col span={18}>
                    <Text>{confirmedBookingData?.data?.booking_number}</Text>
                  </Col>
                </Row>
              </Flex>
              <Divider />
              <Text fw="500">{t("embedded.booking_confirmation.guide_information")}</Text>
              <Row gutter={16} className="mt-s">
                <Col span={8}>
                  <Space>
                    <Icon type="feather" name="User" />
                    <Text>{confirmedBookingData?.data?.trip?.guide?.full_name}</Text>
                  </Space>
                </Col>
              </Row>
              <Divider />
              <Flex vertical>
                <Text fw="500" className="mb-s">
                  Cancellation Policy
                </Text>
                <Text>
                  Free cancellation before 48 hours prior to trip due to our{" "}
                  <a href="/#/cancellation-policy" target="_blank" rel="noopener noreferrer" className={styles.linkButton}>
                    Cancellation policy
                  </a>
                </Text>
              </Flex>
            </Card>
            <a
              href={confirmedBookingData?.data?.booking_invoice}
              download="invoice.pdf"
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none" }}
            >
              <Button variant="outlined" color="primary" className="p-l mt-s">
                {t("embedded.booking_confirmation.download_pdf")}
              </Button>
            </a>
            &nbsp;
            {bookingInfo?.data?.guest_token && !loggedIn ? (
              <Button variant="outlined" color="primary" className="p-l mt-s" onClick={handleClick}>
                {t("embedded.booking_confirmation.send_instruction")}
              </Button>
            ) : (
              ""
            )}
          </Col>
          <Col lg={8} xl={9} span={24}>
            <Flex vertical gap={24} className="">
              <Card style={{ background: "#F9FBFB", borderRadius: 16 }} className="p-xs border-0">
                <Title level={4}>{t("embedded.booking_confirmation.price_breakdown")}</Title>
                <Flex vertical gap={4} className="mt-m">
                  {summaryDetails
                    ?.filter(({ value }) => value !== undefined && value !== null && value !== "")
                    ?.map(({ label, value }, idx) => (
                      <Flex justify="space-between" key={label + idx}>
                        <Text>{label}</Text>
                        <Text>{numberToCurrency(value)}</Text>
                      </Flex>
                    ))}
                  <Flex justify="space-between">
                    <Text fw="700">{t("embedded.booking_confirmation.total")}</Text>
                    <Text fw="700">{numberToCurrency(confirmedBookingData?.data?.booked_amount)}</Text>
                  </Flex>
                </Flex>
              </Card>

              {confirmedBookingData?.data?.deposit_amount > 0 && (
                <Card style={{ background: "#F9FBFB", borderRadius: 16 }} className="p-xs border-0">
                  <Title level={4}>{t("embedded.booking_confirmation.payment")}</Title>
                  <Row justify="space-between" className="mb-xs mt-m">
                    <Text fw="700">
                      {confirmedBookingData?.data?.payment_method?.toLowerCase() === "paypal"
                        ? t("embedded.booking_confirmation.paid_amount_paypal", {
                            paymentMethod: confirmedBookingData?.data?.payment_method
                          })
                        : t("embedded.booking_confirmation.paid_amount_info", {
                            paymentMethod: confirmedBookingData?.data?.payment_method,
                            lastFour: confirmedBookingData?.data?.last_four
                          })}
                    </Text>
                    <Text fw="700">{numberToCurrency(confirmedBookingData?.data?.deposit_amount)}</Text>
                  </Row>
                  {(confirmedBookingData?.data?.payment_option === "deposit_and_balance_in_cash" ||
                    confirmedBookingData?.data?.payment_option === "deposit_and_balance_later") && (
                    <Flex align="center" justify="space-between">
                      <Text>
                        {confirmedBookingData?.data?.payment_option === "deposit_and_balance_later"
                          ? `Balance Due on ${moment(confirmedBookingData?.data?.booked_date).subtract(1, "day").format("MMMM D, YYYY")}`
                          : `Balance Due on ${moment(confirmedBookingData?.data?.booked_date).format("MMMM D, YYYY")}`}
                      </Text>
                      <Text>{numberToCurrency(balanceAmount)}</Text>
                    </Flex>
                  )}
                  {confirmedBookingData?.data?.status === "booked" ? (
                    ""
                  ) : (
                    <Text type="secondary">{t("embedded.booking_confirmation.no_charge")}</Text>
                  )}
                </Card>
              )}
            </Flex>
          </Col>
        </Row>
        {confirmedBookingData?.data?.guide_near_by_trips?.length > 0 && (
          <Flex vertical className="mt-4xl">
            <Title level={4}>
              {t("embedded.booking_confirmation.other_experiences_near", { trip_city: confirmedBookingData?.data?.trip_city })}
            </Title>
            <div className={`mt-xl ${styles.grid}`}>
              {confirmedBookingData?.data?.guide_near_by_trips?.map((exp, index) => (
                <EmbeddedExperienceCard
                  key={index + 1}
                  image={exp?.media?.find((item) => item?.position === 0)?.preview_url}
                  title={exp?.title}
                  location={exp?.address}
                  time={exp?.available_hours_range}
                  people={exp?.max_guests}
                  price={exp?.price_per_person}
                  experienceId={exp?.uuid}
                  mode={mode}
                />
              ))}
            </div>
          </Flex>
        )}
      </EmbeddedBookingLayout>
    </>
  )
}

BookingConfirmation.propTypes = {
  mode: PropTypes.string
}

export default BookingConfirmation
